import cn from 'clsx'

// Components
import { IconTrophy } from '@tracklib/kit'
import { IconBox } from 'kit'
import CenteredDescriptionBlock, { CenteredDescriptionBlockType } from 'components/staticpages/blocks/centereddescriptionblock'

import styles from './prizesblock.css'

const getPrizeSpecifics = (prizeOrder: number): { orderDesc: string; prizeColor: string } => {
  let orderDesc = ''
  let prizeColor = ''
  switch (prizeOrder) {
    case 1:
      orderDesc = 'First prize'
      prizeColor = '#fdd444'
      break
    case 2:
      orderDesc = 'Second prize'
      prizeColor = '#b2aeae'
      break
    case 3:
      orderDesc = 'Third prize'
      prizeColor = '#cd7f32'
      break
    case 4:
      orderDesc = 'Fourth prize'
      prizeColor = '#e92662'
      break
    case 5:
      orderDesc = 'Fifth prize'
      prizeColor = '#523424'
      break
    default:
      break
  }

  return {
    orderDesc,
    prizeColor,
  }
}

type PrizeProps = {
  image: number
  title: string
  description: string
  prize_order: number
}

export const Prize = ({ image, title, description, prize_order }: PrizeProps): JSX.Element => {
  const { orderDesc, prizeColor } = getPrizeSpecifics(prize_order)
  return (
    <div className={styles.prizeWrapper}>
      <IconBox imageUrl={`/is/${image}/fill-368x368/`} icon={<IconTrophy fill={prizeColor} />} className={styles.iconBox}>
        <span className={styles.prizeContent}>
          <span className={styles.prizeOrder}>{orderDesc}</span>
          <span className={styles.title}>{title}</span>
          <span className={styles.description}>{description}</span>
        </span>
      </IconBox>
    </div>
  )
}

type SecondaryPrizeProps = {
  image: number
  description: string
}

export const SecondaryPrize = ({ image, description }: SecondaryPrizeProps): JSX.Element => (
  <div className={styles.secondaryPrize}>
    <div className={styles.imageWrapper}>
      <img className={styles.image} src={`/is/${image}/fill-1000x0/`} alt="" />
    </div>
    <div className={styles.secondaryPrizeDesc}>{description}</div>
  </div>
)

type PrizesBlockProps = {
  value: {
    prizes: Array<PrizeProps>
    centered_description: typeof CenteredDescriptionBlockType
    secondary_prizes_description: string
    secondary_prizes: Array<{
      image: number
      description: string
    }>
    third_prizes_description: string
    third_prizes: Array<{
      image: number
      description: string
    }>
  }
}

const PrizesBlock = (props: PrizesBlockProps): JSX.Element => {
  const {
    value: { prizes, centered_description, secondary_prizes_description, secondary_prizes, third_prizes_description, third_prizes },
  } = props

  const secondaryDescParts = secondary_prizes_description.split('|')
  const thirdDescParts = third_prizes_description.split('|')
  const secondaryPrize1 = secondary_prizes[0]
  const secondaryPrize2 = secondary_prizes[1]
  const thirdPrize1 = third_prizes[0]
  const thirdPrize2 = third_prizes[1]

  return (
    <div className={styles.background}>
      <div className={styles.content}>
        {centered_description && <CenteredDescriptionBlock value={centered_description} />}
        <div className={styles.prizesWrapper}>
          {prizes.map((prize, index) => (
            <Prize
              image={prize.image}
              title={prize.title}
              key={String(index)}
              description={prize.description}
              prize_order={prize.prize_order}
            />
          ))}
        </div>
        <div className={styles.secondaryPrizes}>
          <div className={cn(styles.descWrapper, styles.noMobile)}>
            {secondaryDescParts.map((descPart, index) => (
              <span className={styles.descPart} key={String(index)}>
                {descPart}
              </span>
            ))}
          </div>
          <div className={cn(styles.descWrapper, styles.mobileOnly)}>{secondary_prizes_description.split('|').join('')}</div>
          <div className={styles.secondaryPrizesWrapper}>
            <SecondaryPrize image={secondaryPrize1.image} description={secondaryPrize1.description} />
            {secondaryPrize2 && <div className={styles.plus}>+</div>}
            {secondaryPrize2 && <SecondaryPrize image={secondaryPrize2.image} description={secondaryPrize2.description} />}
          </div>
        </div>

        <div className={styles.secondaryPrizes}>
          <div className={cn(styles.descWrapper, styles.noMobile)}>
            {thirdDescParts.map((descPart, index) => (
              <span className={styles.descPart} key={String(index)}>
                {descPart}
              </span>
            ))}
          </div>
          <div className={cn(styles.descWrapper, styles.mobileOnly)}>{third_prizes_description.split('|').join('')}</div>
          <div className={styles.secondaryPrizesWrapper}>
            <SecondaryPrize image={thirdPrize1.image} description={thirdPrize1.description} />
            {thirdPrize2 && <div className={styles.plus}>+</div>}
            {thirdPrize2 && <SecondaryPrize image={thirdPrize2.image} description={thirdPrize2.description} />}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrizesBlock
